// ============================================
// pages/reports/OperationalProformas.tsx
// MD/CAO read-only view of the 3 operational proformas:
//   1. Unloading Bills (godown × H&T contractor)
//   2. Transport Trips → Bills (PC → godown movement, payable bills)
//   3. Gunny Utilization (society-wise bags supplied/utilized/returned)
//
// Mirrors the structure of docs/procurement proformas.xlsx — read-only,
// print-friendly. The matching list pages under /unloading, /transportation/billing,
// /gunny-bags/utilization are where data is captured & approved.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { FileSpreadsheet, Loader2, Truck, Package, Receipt } from 'lucide-react';
import {
  seasonsAPI, districtsAPI, commoditiesAPI,
  unloadingBillsAPI, transportBillsAPI, gunnyUtilizationAPI,
} from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season, District } from '../../types/markfed';

type Tab = 'unloading' | 'transport' | 'gunny';

export const OperationalProformas: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [districtFilter, setDistrictFilter] = useState<number>(0);
  const [commodityFilter, setCommodityFilter] = useState<number>(0);

  const [unloading, setUnloading] = useState<any[]>([]);
  const [transport, setTransport] = useState<any[]>([]);
  const [gunny, setGunny] = useState<any[]>([]);

  const [activeTab, setActiveTab] = useState<Tab>('unloading');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const districtMap = useMemo(() => {
    const m: Record<number, string> = {};
    districts.forEach((d) => { m[d.id] = d.name; });
    return m;
  }, [districts]);

  const commodityMap = useMemo(() => {
    const m: Record<number, string> = {};
    commodities.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [commodities]);

  useEffect(() => {
    (async () => {
      try {
        const [s, d, c] = await Promise.all([
          seasonsAPI.list(), districtsAPI.list(), commoditiesAPI.list(),
        ]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setDistricts(d.data);
        setCommodities(c.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load reference data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (commodityFilter) params.commodity_id = commodityFilter;
      if (districtFilter) params.district_id = districtFilter;
      try {
        const [u, t, g] = await Promise.allSettled([
          unloadingBillsAPI.list(selectedSeasonId, params),
          transportBillsAPI.list(selectedSeasonId, params),
          gunnyUtilizationAPI.list(selectedSeasonId, params),
        ]);
        setUnloading(u.status === 'fulfilled' ? (Array.isArray(u.value.data) ? u.value.data : []) : []);
        setTransport(t.status === 'fulfilled' ? (Array.isArray(t.value.data) ? t.value.data : []) : []);
        setGunny(g.status === 'fulfilled' ? (Array.isArray(g.value.data) ? g.value.data : []) : []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load proformas');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId, commodityFilter, districtFilter]);

  const formatDate = (s?: string) => {
    if (!s) return '--';
    try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return s; }
  };

  // ─── Totals (footer rows) ─────────────────────────────────────
  const unloadingTotals = useMemo(() => unloading.reduce((acc, b) => {
    acc.bags += num(b.num_bags_claimed);
    acc.bill += num(b.bill_amount);
    acc.asor += num(b.asor_charges);
    acc.total += num(b.total_bill_claimed);
    acc.mts += num(b.qty_mts);
    return acc;
  }, { bags: 0, bill: 0, asor: 0, total: 0, mts: 0 }), [unloading]);

  const transportTotals = useMemo(() => transport.reduce((acc, b) => {
    acc.trips += num(b.total_trips);
    acc.bags += num(b.total_bags);
    acc.weight += num(b.total_weight_qtl);
    acc.claimed += num(b.total_amount_claimed);
    acc.verified += num(b.total_amount_verified);
    acc.released += num(b.payment_released);
    return acc;
  }, { trips: 0, bags: 0, weight: 0, claimed: 0, verified: 0, released: 0 }), [transport]);

  const gunnyTotals = useMemo(() => gunny.reduce((acc, r) => {
    acc.supplied += num(r.bags_supplied);
    acc.utilized += num(r.bags_utilized);
    acc.returned += num(r.bags_returned);
    acc.net += num(r.net_amount);
    acc.gross += num(r.gross_amount);
    acc.released += num(r.payment_released);
    return acc;
  }, { supplied: 0, utilized: 0, returned: 0, net: 0, gross: 0, released: 0 }), [gunny]);

  const handlePrint = () => window.print();

  if (loading && unloading.length === 0 && transport.length === 0 && gunny.length === 0) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        <span className="ml-2 text-gray-500">Loading proformas...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto print:max-w-none">
      <div className="mb-6 flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FileSpreadsheet className="w-7 h-7 text-blue-600" />
            Operational Proformas
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Read-only MD/CAO view of unloading, transport, and gunny utilization proformas
          </p>
        </div>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm"
        >
          Print
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4 print:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
            <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Commodity</label>
            <select value={commodityFilter} onChange={(e) => setCommodityFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value={0}>All Commodities</option>
              {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">District</label>
            <select value={districtFilter} onChange={(e) => setDistrictFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value={0}>All Districts</option>
              {districts.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex gap-2 border-b border-gray-200 print:hidden">
        {([
          ['unloading', 'Unloading Bills', Receipt, unloading.length],
          ['transport', 'Transport Bills', Truck, transport.length],
          ['gunny', 'Gunny Utilization', Package, gunny.length],
        ] as Array<[Tab, string, any, number]>).map(([id, label, Icon, count]) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg ${
              activeTab === id
                ? 'bg-white border-x border-t border-gray-200 text-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded-full">{count}</span>
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* ─── 1. Unloading Bills ─────────────────────────────── */}
      {activeTab === 'unloading' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border-0">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h2 className="text-sm font-bold text-gray-800">
              Statement showing the unloading bills claimed by the H&amp;T contractors
            </h2>
            <p className="text-xs text-gray-500">Source: docs/procurement proformas.xlsx → "unloading" sheet</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">#</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Commodity</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Godown Type</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Agreement Date</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Godown</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">H&amp;T Contractor</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Invoice No</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Bill Date</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Bags</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Rate/Bag</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Bill Amt</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">ASOR</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Total</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Qty (MT)</th>
                  <th className="px-2 py-2 text-center font-semibold text-gray-600 uppercase">WH Cert.</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">PAN</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {unloading.length === 0 ? (
                  <tr><td colSpan={16} className="px-4 py-8 text-center text-gray-400">No unloading bills</td></tr>
                ) : (
                  unloading.map((b, i) => (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-gray-500">{i + 1}</td>
                      <td className="px-2 py-1.5 text-gray-700">{b.commodity?.name || commodityMap[b.commodity_id] || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-700">{b.godown_type || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{formatDate(b.agreement_date)}</td>
                      <td className="px-2 py-1.5 text-gray-700">{b.godown?.name || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-700">{b.contractor?.firm_name || b.contractor?.contractor_name || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-700 font-mono">{b.invoice_no || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{formatDate(b.bill_date)}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{num(b.num_bags_claimed).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{num(b.rate_per_bag).toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right text-gray-900">{formatAmount(num(b.bill_amount))}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{formatAmount(num(b.asor_charges))}</td>
                      <td className="px-2 py-1.5 text-right font-medium text-gray-900">{formatAmount(num(b.total_bill_claimed))}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{num(b.qty_mts).toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          b.warehouse_certified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {b.warehouse_certified ? 'YES' : 'NO'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-gray-700 font-mono">{b.contractor?.pan_number || '--'}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {unloading.length > 0 && (
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr className="font-bold">
                    <td colSpan={8} className="px-2 py-2 text-right text-gray-700">TOTAL</td>
                    <td className="px-2 py-2 text-right">{unloadingTotals.bags.toLocaleString('en-IN')}</td>
                    <td></td>
                    <td className="px-2 py-2 text-right">{formatAmount(unloadingTotals.bill)}</td>
                    <td className="px-2 py-2 text-right">{formatAmount(unloadingTotals.asor)}</td>
                    <td className="px-2 py-2 text-right">{formatAmount(unloadingTotals.total)}</td>
                    <td className="px-2 py-2 text-right">{unloadingTotals.mts.toFixed(2)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* ─── 2. Transport Bills ─────────────────────────────── */}
      {activeTab === 'transport' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border-0">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h2 className="text-sm font-bold text-gray-800">
              Transportation bill verification — Centre to Godown
            </h2>
            <p className="text-xs text-gray-500">Source: docs/procurement proformas.xlsx → "trasport" sheet</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">#</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">District</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Commodity</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Transporter</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Bill No</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Bill Date</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Trips</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Bags</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Weight (Qtl)</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Claimed</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Verified</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">TDS</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Released</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transport.length === 0 ? (
                  <tr><td colSpan={14} className="px-4 py-8 text-center text-gray-400">No transport bills</td></tr>
                ) : (
                  transport.map((b, i) => (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-gray-500">{i + 1}</td>
                      <td className="px-2 py-1.5 text-gray-700">{b.district?.name || districtMap[b.district_id] || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-700">{b.commodity?.name || commodityMap[b.commodity_id] || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-700">{b.contractor?.firm_name || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-700 font-mono">{b.bill_no || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{formatDate(b.bill_date)}</td>
                      <td className="px-2 py-1.5 text-right">{num(b.total_trips).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-right">{num(b.total_bags).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-right">{num(b.total_weight_qtl).toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right">{formatAmount(num(b.total_amount_claimed))}</td>
                      <td className="px-2 py-1.5 text-right font-medium text-gray-900">{formatAmount(num(b.total_amount_verified))}</td>
                      <td className="px-2 py-1.5 text-right">{formatAmount(num(b.tds_amount))}</td>
                      <td className="px-2 py-1.5 text-right text-green-700">{formatAmount(num(b.payment_released))}</td>
                      <td className="px-2 py-1.5 text-right text-orange-600">{formatAmount(num(b.balance_to_release))}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {transport.length > 0 && (
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr className="font-bold">
                    <td colSpan={6} className="px-2 py-2 text-right text-gray-700">TOTAL</td>
                    <td className="px-2 py-2 text-right">{transportTotals.trips.toLocaleString('en-IN')}</td>
                    <td className="px-2 py-2 text-right">{transportTotals.bags.toLocaleString('en-IN')}</td>
                    <td className="px-2 py-2 text-right">{transportTotals.weight.toFixed(2)}</td>
                    <td className="px-2 py-2 text-right">{formatAmount(transportTotals.claimed)}</td>
                    <td className="px-2 py-2 text-right">{formatAmount(transportTotals.verified)}</td>
                    <td></td>
                    <td className="px-2 py-2 text-right text-green-700">{formatAmount(transportTotals.released)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* ─── 3. Gunny Utilization ──────────────────────────── */}
      {activeTab === 'gunny' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border-0">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h2 className="text-sm font-bold text-gray-800">
              Statement showing the gunny bag utilization, supplier invoices &amp; payments
            </h2>
            <p className="text-xs text-gray-500">Source: docs/procurement proformas.xlsx → "gunnies" sheet</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">#</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">District</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Supplier</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Tender ID</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Society</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Supplied</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Utilized</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Returned</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Inv No</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Inv Date</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">E-Way No</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Net Amt</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Gross</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Released</th>
                  <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {gunny.length === 0 ? (
                  <tr><td colSpan={15} className="px-4 py-8 text-center text-gray-400">No gunny utilization records</td></tr>
                ) : (
                  gunny.map((r, i) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-gray-500">{i + 1}</td>
                      <td className="px-2 py-1.5 text-gray-700">{r.district?.name || districtMap[r.district_id] || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-700">{r.supplier?.supplier_name || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-600 font-mono">{r.supplier?.tender_id || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-700">{r.society_name || '--'}</td>
                      <td className="px-2 py-1.5 text-right">{num(r.bags_supplied).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-right">{num(r.bags_utilized).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-right">{num(r.bags_returned).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-gray-700 font-mono">{r.supplier_invoice_no || '--'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{formatDate(r.supplier_invoice_date)}</td>
                      <td className="px-2 py-1.5 text-gray-700 font-mono">{r.eway_bill_no || '--'}</td>
                      <td className="px-2 py-1.5 text-right">{formatAmount(num(r.net_amount))}</td>
                      <td className="px-2 py-1.5 text-right font-medium text-gray-900">{formatAmount(num(r.gross_amount))}</td>
                      <td className="px-2 py-1.5 text-right text-green-700">{formatAmount(num(r.payment_released))}</td>
                      <td className="px-2 py-1.5 text-right text-orange-600">{formatAmount(num(r.balance_to_release))}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {gunny.length > 0 && (
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr className="font-bold">
                    <td colSpan={5} className="px-2 py-2 text-right text-gray-700">TOTAL</td>
                    <td className="px-2 py-2 text-right">{gunnyTotals.supplied.toLocaleString('en-IN')}</td>
                    <td className="px-2 py-2 text-right">{gunnyTotals.utilized.toLocaleString('en-IN')}</td>
                    <td className="px-2 py-2 text-right">{gunnyTotals.returned.toLocaleString('en-IN')}</td>
                    <td colSpan={3}></td>
                    <td className="px-2 py-2 text-right">{formatAmount(gunnyTotals.net)}</td>
                    <td className="px-2 py-2 text-right">{formatAmount(gunnyTotals.gross)}</td>
                    <td className="px-2 py-2 text-right text-green-700">{formatAmount(gunnyTotals.released)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationalProformas;
